import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  InjectionToken,
  Output,
  inject,
  signal,
} from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'

import { Select } from 'primeng/select'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxFormControlModule,
  FlxModalModule,
  FlxModalService,
  SelectItem,
} from '@flowx/angular-ui-toolkit'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import { FlxAutofocusDirective } from '@flowx/designer/shared/directives/autofocus.directive'
import { EnterSubmitFormDirective } from '@flowx/designer/shared/directives/enter-submit-form.directive'
import { AppBranch, ApplicationStatus } from '@flowx/designer/shared/entities/application.entity'
import { trimmed } from '@flowx/designer/shared/validators'
import { BranchNameComponent } from '@flowx/designer/shared/versioning/branch-name/branch-name.component'

@Component({
  selector: 'app-merge-branch-modal',
  templateUrl: './merge-branch-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FlxModalModule,
    FlxFormControlModule,
    ReactiveFormsModule,
    FlxButtonModule,
    FlxSpinnerModule,
    FlxAutofocusDirective,
    EnterSubmitFormDirective,
    BranchNameComponent,
    Select,
  ],
})
export class MergeBranchModalComponent {
  readonly #modalService = inject(FlxModalService)
  readonly #data = inject(
    FLX_MODAL_DATA as InjectionToken<{ applicationVersionName: string; branches: AppBranch[] }>
  )
  #selectableBranches: SelectItem<AppBranch>[] = this.#data.branches
    .filter((branch) => branch.lastCommitStatus === ApplicationStatus.COMMITTED)
    .map((branch) => ({ label: branch.name, value: branch }))
  @Output() dataEventEmitter = new EventEmitter<any>()

  branches: Array<SelectItem<AppBranch> & { disabled: boolean }> = this.#data.branches.map(
    (branch) => ({
      label: branch.name,
      value: branch,
      disabled: branch.lastCommitStatus !== ApplicationStatus.COMMITTED,
    })
  )
  maxLength = 50
  descriptionMaxLength = 2000
  processDefinitionId: number

  mergeFG: FormGroup<{
    branchName: FormControl<SelectItem<AppBranch>>
    submitMessage: FormControl<string>
    description: FormControl<string>
  }> = new FormGroup({
    branchName: new FormControl<SelectItem<AppBranch>>(
      {
        value: this.#selectableBranches.length === 1 ? this.#selectableBranches[0] : null,
        disabled: this.#selectableBranches.length === 1,
      },
      [Validators.required]
    ),
    submitMessage: new FormControl<string>('', [
      trimmed(Validators.required),
      trimmed(Validators.minLength(2)),
      Validators.maxLength(this.maxLength),
    ]),
    description: new FormControl<string>('', [Validators.maxLength(this.descriptionMaxLength)]),
  })

  get branchNameFC(): FormControl {
    return this.mergeFG.get('branchName') as FormControl
  }

  get submitMessageFC(): FormControl {
    return this.mergeFG.get('submitMessage') as FormControl
  }

  get descriptionFC(): FormControl {
    return this.mergeFG.get('description') as FormControl
  }

  modalTitle = `Merge Branch ${this.#data.applicationVersionName}`
  submitted = signal(false)

  mergeBranch(): void {
    this.submitted.set(true)
    if (this.mergeFG.invalid) {
      return
    }
    this.dataEventEmitter.emit({
      intoBranchName: this.branchNameFC.value?.label || this.branchNameFC.value,
      commitMessage: this.submitMessageFC.value.trim(),
      commitDescription: this.descriptionFC.value?.trim() || undefined,
    })
    this.closeModal()
  }

  closeModal(): void {
    this.#modalService.hide()
  }
}
